/**
 * Level 2 chart region — conduct-score plot (volume bars + score line on its own
 * right axis) or detriment-mix plot (share of the three, framed on the observed
 * range). Grain and plot are `CHART` toggles; every bucket is a click target
 * that scopes the panels below. See SPEC §6.2.1.
 */

import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { cn } from '../../../../../lib/utils';
import { DETRIMENT_META, GO_GOOD_VIBES } from '../constants';
import type { Grain, Plot, SeriesPoint } from '../types';
import { bucketShares, fmt, recordsLabel, scoreAxis, shareAxis } from '../utils';

const GRAINS: Array<{ key: Grain; label: string }> = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

const PLOTS: Array<{ key: Plot; label: string }> = [
  { key: 'score', label: 'Conduct score' },
  { key: 'detriment', label: 'Detriment mix' },
];

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ key: T; label: string; disabled?: boolean }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-full bg-muted p-[3px]">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          disabled={o.disabled}
          onClick={() => !o.disabled && onChange(o.key)}
          className={cn(
            'rounded-full px-3 py-1.5 text-xs font-bold transition-all',
            o.disabled
              ? 'cursor-not-allowed text-muted-foreground/40 line-through'
              : value === o.key
                ? 'bg-background text-[var(--go-good-vibes)] shadow-[0_1px_2px_rgba(0,0,0,.06)]'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface ChartRow {
  label: string;
  bucketStart: string;
  volume: number;
  score: number;
  diss: number;
  strain: number;
  harm: number;
}

function ChartTooltip({ active, payload, plot }: any) {
  if (!active || !payload?.length) return null;
  const row: ChartRow = payload[0].payload;
  return (
    <div className="rounded-2xl border border-border/70 bg-background/95 p-3 text-xs shadow-[var(--shadow-card)] backdrop-blur-sm">
      <div className="font-bold">{row.label}</div>
      {plot === 'score' ? (
        <div className="mt-1 space-y-0.5 text-muted-foreground">
          <div>{recordsLabel(row.volume)}</div>
          <div>Conduct score {row.score.toFixed(1)}</div>
        </div>
      ) : (
        <div className="mt-1 space-y-0.5">
          {DETRIMENT_META.map((d, i) => (
            <div key={d.type} className="flex items-center gap-1.5" style={{ color: d.color }}>
              <i className="h-2 w-2 rounded-full" style={{ background: d.color }} />
              {d.label} {[row.diss, row.strain, row.harm][i].toFixed(0)}%
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function OutcomeChart({
  series,
  grain,
  allowedGrains,
  plot,
  selectedBucket,
  periodLabel,
  onGrainChange,
  onPlotChange,
  onSelectBucket,
}: {
  series: SeriesPoint[];
  grain: Grain;
  allowedGrains: Grain[];
  plot: Plot;
  selectedBucket: string | null;
  periodLabel: string;
  onGrainChange: (g: Grain) => void;
  onPlotChange: (p: Plot) => void;
  onSelectBucket: (bucketStart: string | null) => void;
}) {
  const isMix = plot === 'detriment';

  const { rows, sAxis, shAxis } = useMemo(() => {
    const rows: ChartRow[] = series.map((p) => {
      const [diss, strain, harm] = bucketShares(p.detriment);
      return {
        label: p.label,
        bucketStart: p.bucketStart,
        volume: p.volume,
        score: p.score,
        diss,
        strain,
        harm,
      };
    });
    const sAxis = scoreAxis(series.map((p) => p.score));
    const shAxis = shareAxis(rows.flatMap((r) => [r.diss, r.strain, r.harm]));
    return { rows, sAxis, shAxis };
  }, [series]);

  const last = rows[rows.length - 1];

  const handleClick = (state: any) => {
    if (state == null) return;
    const idx = state.activeTooltipIndex;
    if (idx == null || idx < 0 || idx >= rows.length) return;
    const bs = rows[idx].bucketStart;
    onSelectBucket(selectedBucket === bs ? null : bs);
  };

  const title = `${isMix ? 'Detriment mix' : 'Conduct score'} — ${periodLabel}`;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
          {title}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <Segmented
            value={grain}
            onChange={onGrainChange}
            options={GRAINS.map((g) => ({ ...g, disabled: !allowedGrains.includes(g.key) }))}
          />
          <Segmented value={plot} onChange={onPlotChange} options={PLOTS} />
        </div>
      </div>

      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={rows}
            margin={{ top: 12, right: 8, bottom: 0, left: 0 }}
            onClick={handleClick}
          >
            <CartesianGrid vertical={false} stroke="var(--soft,#ECECEE)" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              tick={{ fontSize: 9, fontWeight: 700, fill: 'hsl(var(--muted-foreground))' }}
            />
            {/* recharts only inspects its DIRECT children for axes/series — it
                does NOT recurse into React fragments — so each axis/series below
                is rendered as a bare conditional child, never wrapped in <>…</>. */}
            {isMix && (
              <YAxis
                domain={[shAxis.lo, shAxis.hi]}
                tickLine={false}
                axisLine={false}
                width={34}
                tick={{ fontSize: 9, fontWeight: 700, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v: number) => `${v}%`}
              />
            )}
            {!isMix && (
              <YAxis
                yAxisId="volume"
                tickLine={false}
                axisLine={false}
                width={38}
                tick={{ fontSize: 9, fontWeight: 700, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v: number) => fmt(v)}
              />
            )}
            {!isMix && (
              <YAxis
                yAxisId="score"
                orientation="right"
                domain={[0, sAxis.top]}
                tickLine={false}
                axisLine={false}
                width={30}
                tick={{ fontSize: 9, fontWeight: 700, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(v: number) => v.toFixed(1)}
              />
            )}

            {/* Chart-level onClick yields activeTooltipIndex across the full plot
                width/height while the Tooltip is present — so every bucket is a
                click target, including in the line-only detriment view. */}
            <Tooltip
              cursor={{ fill: 'rgba(67,66,245,.06)' }}
              content={<ChartTooltip plot={plot} />}
            />

            {!isMix && (
              <Bar yAxisId="volume" dataKey="volume" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {rows.map((r) => (
                  <Cell
                    key={r.bucketStart}
                    fill={GO_GOOD_VIBES}
                    fillOpacity={selectedBucket === r.bucketStart ? 0.55 : 0.28}
                  />
                ))}
              </Bar>
            )}
            {!isMix && (
              <ReferenceLine
                yAxisId="score"
                y={sAxis.edge}
                stroke="var(--band-mod,#C99D2C)"
                strokeDasharray="4 3"
                strokeOpacity={0.55}
                label={{
                  value: sAxis.edgeLabel,
                  position: 'insideTopRight',
                  fontSize: 9,
                  fontWeight: 800,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            )}
            {!isMix && (
              <Line
                yAxisId="score"
                type="monotone"
                dataKey="score"
                stroke={GO_GOOD_VIBES}
                strokeWidth={2.2}
                dot={{ r: 3, fill: GO_GOOD_VIBES, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            )}

            {isMix &&
              DETRIMENT_META.map((d, i) => (
                <Line
                  key={d.type}
                  type="monotone"
                  dataKey={(['diss', 'strain', 'harm'] as const)[i]}
                  stroke={d.color}
                  strokeWidth={2.2}
                  dot={{ r: 2.5, fill: d.color, strokeWidth: 0 }}
                  isAnimationActive={false}
                />
              ))}

            {selectedBucket && (
              <ReferenceLine
                yAxisId={isMix ? undefined : 'volume'}
                x={rows.find((r) => r.bucketStart === selectedBucket)?.label}
                stroke={GO_GOOD_VIBES}
                strokeWidth={1.5}
                strokeOpacity={0.5}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* legend */}
      <div className="mt-3 flex flex-wrap gap-4 border-t border-[var(--soft,#ECECEE)] pt-3 text-[10px] font-bold text-muted-foreground">
        {isMix ? (
          <>
            {DETRIMENT_META.map((d, i) => (
              <span key={d.type} className="flex items-center gap-1.5">
                <i className="h-[3px] w-3.5 rounded-full" style={{ background: d.color }} />
                {d.label}
                <b className="ml-0.5 text-foreground">
                  {last ? [last.diss, last.strain, last.harm][i].toFixed(0) : '0'}%
                </b>
              </span>
            ))}
            <span className="font-semibold">Share of the three, so a shift shows even when volume is flat</span>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm" style={{ background: GO_GOOD_VIBES, opacity: 0.28 }} />
              Records
            </span>
            <span className="flex items-center gap-1.5">
              <i className="h-2.5 w-2.5 rounded-sm" style={{ background: GO_GOOD_VIBES }} />
              Conduct score, right axis (max {sAxis.top.toFixed(1)})
            </span>
            <span className="font-semibold">
              Dashed line marks the {sAxis.edge === 3.6 ? 'moderate' : 'high'}-band boundary ({sAxis.edge})
            </span>
          </>
        )}
      </div>
    </div>
  );
}
